import React, { useContext, useEffect, useState } from "react";
import { Grid, TextField, Typography, Button, IconButton, Box, Checkbox, Link } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { LeaguePlatform } from "src/mockup/UserPreferencesData";
import { WidgetContext } from "src/contexts/WidgetContext";
import { Browser } from "@capacitor/browser";
import { mobile } from "src/utils/platformUtils";
import { getYahooAuthUrl } from "src/api/getYahooAuthUrl";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { Capacitor } from "@capacitor/core";
import LoginFormText from "./LoginFormText";

interface LoginFormYahooProps {
  platformSelected?: LeaguePlatform;
  loginError?: string;
  setLoginError?: (error: string) => void;
  yahooCode?: string;
  setYahooCode?: (code: string) => void;
  handleYahooSubmit?: (event: React.FormEvent) => void;
  isAuthorized: boolean;
  setIsAuthorized: (value: boolean) => void;
  setLoggingInTo: (value: string) => void;
}

const LoginFormYahoo: React.FC<LoginFormYahooProps> = ({
  platformSelected,
  loginError,
  setLoginError,
  yahooCode,
  setYahooCode,
  handleYahooSubmit,
  isAuthorized,
  setIsAuthorized,
  setLoggingInTo,
}) => {
  //state for hide textfield placeholder
  const { setIsYahooLoading, cancelPendingRequests, leaguePlatforms, setLeaguePlatforms } = useContext(WidgetContext);

  const navigate = useNavigate();

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  // Nuevo useEffect para capturar el código de la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get("code");

    if (codeFromUrl) {
      // Establecer el código en el estado
      setYahooCode(codeFromUrl);

      // Opcional: Limpiar la URL después de capturar el código
      // window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // Se ejecuta solo al montar el componente

  //function to get the code from yahoo in external url generated by backend
  const handleYahooGetCodeWithUrl = async (event: React.FormEvent) => {
    console.log("🚀 Starting Yahoo authentication flow");
    event.preventDefault();
    setIsYahooLoading(true);

    try {
      // clean the login error
      setLoginError("");
      //Get the yahoo auth url from the platform selected
      let yahooAuthUrl = platformSelected.yahooAuthUrl?.authUrl;

      // if no auth url, get it from the backend
      if (!yahooAuthUrl) {
        // setLoginError("No valid Yahoo auth URL received, please try again");
        console.log("🔄 No auth URL found, getting a new one");
        const accessAuthToken = await getSafeAccessAuth0Token();
        //guard clause to stop execution if the token is not valid (log out)
        if (!accessAuthToken) {
          return;
        }
        const response = await getYahooAuthUrl(accessAuthToken);
        console.log("✅ Yahoo Auth URL received:", {
          hasResponse: !!response,
          hasAuthUrl: !!response?.authUrl,
          authUrl: response?.authUrl?.substring(0, 50) + "...",
        });

        if (!response?.authUrl) {
          // setLoginError("No valid Yahoo auth URL received, please try again");
          throw new Error("No valid Yahoo auth URL received");
        }

        // update the platforms with the new url
        const newPlatforms = leaguePlatforms.map((platform) => ({
          ...platform,
          yahooAuthUrl: platform.id === "Yahoo" ? response : platform.yahooAuthUrl,
        }));

        setLeaguePlatforms(newPlatforms);
        yahooAuthUrl = response.authUrl;
        console.log("✅ Platforms updated successfully");
      } else {
        console.log("✅ Using existing Yahoo auth URL:", yahooAuthUrl.substring(0, 50) + "...");
      }

      // continue with the normal flow using the url obtained
      console.log("📱 Is mobile device:", mobile);

      if (mobile) {
        const forceLoginUrl = `${yahooAuthUrl}&prompt=login`;

        console.log("📱 Opening Capacitor Browser for Yahoo auth", forceLoginUrl);
        await Browser.open({
          url: forceLoginUrl,
          windowName: "_self",
          presentationStyle: "popover",
        });

        Browser.addListener("browserFinished", async () => {
          await new Promise((resolve) => setTimeout(resolve, 500));

          // only close the loading if we don't receive callback
          const currentUrl = window.location.href;
          if (!currentUrl.includes("yahoo_callback")) {
            setIsYahooLoading(false);
            setLoggingInTo(null);
            cancelPendingRequests();
            // Redirect to appropriate path based on Auth0 authentication status
            const redirectPath = isAuthenticated ? "/home" : "/";
            navigate(redirectPath, { replace: true });

            // make sure yahoo is not appear as connected if user close the browser whitout login
            const updatedPlatforms = leaguePlatforms.map((platform) => {
              if (platform.id === "Yahoo") {
                return {
                  ...platform,
                  isLoggedIn: false,
                  isSelected: false,
                };
              }
              return platform;
            });
            setLeaguePlatforms(updatedPlatforms);
          }
        });
      } else {
        console.log("🔗 Redirecting in web:", yahooAuthUrl);
        const forceLoginUrl = `${yahooAuthUrl}&prompt=login`;
        window.location.href = forceLoginUrl;
      }

      handleYahooSubmit(event);
    } catch (error) {
      // setLoginError("Error in Yahoo auth flow");
      console.error("Error in Yahoo auth flow:", error);
      setIsYahooLoading(false);
    }
  };

  // console.log("platformSelected", platformSelected);

  return (
    <Grid container spacing={0} justifyContent="center" sx={{ padding: 0 }}>
      <Grid item xs={10} sx={{ textAlign: "center", pb: 1, pt: 0 }}>
        <Typography sx={{ position: "relative", pb: 1 }} variant="h4" color={PureLightThemeCaaS.colors.alpha.orange[100]} textAlign="center">
          {loginError}
        </Typography>

        {/* CUANDO AGREGUEMOS IMAGENES DE LOGOS REALES DE NUEVO, ELIMINAR EL BOX Y DEJAR LAS IMG DE YAHOO */}
        <Typography variant="h3" sx={{ color: "#000000", fontWeight: "bold" }}>
          Yahoo
        </Typography>
        {/* <Box sx={{ visibility: "hidden" }}>
          <img
            src={platformSelected.img}
            alt="Yahoo logo"
            style={{
              width: "140px",
              // aspectRatio: "3/2",
              objectFit: "contain",
            }}
          />
        </Box> */}
      </Grid>

      {/* Authorization text and checkbox */}
      <Box width="80%" sx={{ margin: "auto", pb: 3 }}>
        <Box display="flex" alignItems="flex-start">
          <Checkbox
            checked={isAuthorized}
            onChange={(e) => setIsAuthorized(e.target.checked)}
            sx={{
              padding: "0",
              pt: 0.2,
              boxShadow: "none",
              marginRight: 1,
              "&.Mui-checked": {
                color: PureLightThemeCaaS.colors.alpha.black[100],
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
              "& .MuiTouchRipple-root": {
                display: "none",
              },
            }}
          />
          <LoginFormText platformSelected={platformSelected.name} />

        </Box>
      </Box>

      {/* Get Code button */}
      <Grid item xs={12} sx={{ textAlign: "center", mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleYahooGetCodeWithUrl}
          disabled={!isAuthorized}
          size="large"
          sx={{
            bgcolor: "#570DC9",
            borderRadius: "10px",
            // width: { xs: "70%", lg: "30%" },
            width: "320px",
            height: "52px",
            p: 0,
            boxShadow: "1px 3px 4px #999999",
            "&:hover": { bgcolor: "#4a01a3", boxShadow: "1px 3px 4px #999999" },
            "&.Mui-disabled": {
              bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
              color: PureLightThemeCaaS.colors.alpha.black[50],
            },
          }}
        >
          Connect Yahoo to RotoGPT
        </Button>
      </Grid>
    </Grid>
  );
};

export default LoginFormYahoo;
